/**
 * Create Business Dialog
 * Form for creating a new business
 */

import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';

interface CreateBusinessDialogProps {
  open: boolean;
  onClose: () => void;
  onCreate: (data: { name: string; slug: string; timezone: string }) => Promise<void>;
}

export function CreateBusinessDialog({ open, onClose, onCreate }: CreateBusinessDialogProps) {
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [timezone, setTimezone] = useState('America/New_York');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleCreate = async () => {
    if (!name.trim() || !slug.trim()) {
      setError('Name and slug are required');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      await onCreate({ name: name.trim(), slug: slug.trim(), timezone });
      setName('');
      setSlug('');
      setTimezone('America/New_York');
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create business');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create Business</DialogTitle>
      <DialogContent>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: 16 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            label="Business Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            disabled={loading}
          />
          <TextField
            label="Slug"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            fullWidth
            disabled={loading}
            helperText="Unique identifier for the business"
          />
          <TextField
            label="Timezone"
            value={timezone}
            onChange={(e) => setTimezone(e.target.value)}
            fullWidth
            disabled={loading}
            helperText="e.g., America/New_York, Europe/London"
          />
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button onClick={handleCreate} variant="contained" disabled={loading}>
          {loading ? <CircularProgress size={24} /> : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
